import React, { useState, useEffect, useRef } from 'react';
import { Alert } from '@c2fo/liquidity';
import './UserInfo.css';
import { useAuth } from '../../hooks/useAuth';
import { getToken } from '../../utils/token';
import { config } from '../../config';
import { fetchUserByUuid, type User } from '../../services/userService';

export interface UserInfoProps {
  users: Array<{
    id?: number;
    uuid?: string;
    authServiceUuid?: string;
    fullName: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    emailAddress?: string;
    company?: string;
    industry?: string;
    country?: string;
    role?: string;
    isDecisionMaker?: boolean;
    emailVerified?: boolean;
    status?: number;
    created?: string;
    updated?: string;
    primaryPhoneNumber?: string;
    secondaryPhoneNumber?: string;
    source?: string;
    datacenters?: string[];
    lastLogin?: string;
    isAccountLocked?: boolean;
  }>;
  count: number;
  context?: string;
}

export const UserInfo: React.FC<UserInfoProps> = ({ users, count, context }) => {
  const { canImpersonate } = useAuth();
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [impersonationError, setImpersonationError] = useState<string | null>(null);
  const [loadingUsers, setLoadingUsers] = useState<Record<string, boolean>>({});
  const [fetchedUsers, setFetchedUsers] = useState<Record<string, User>>({});
  const fetchedUsersRef = useRef<Record<string, User>>({});
  const loadingUsersRef = useRef<Record<string, boolean>>({});

  fetchedUsersRef.current = fetchedUsers;
  loadingUsersRef.current = loadingUsers;

  useEffect(() => {
    const abortController = new AbortController();

    const fetchMissingUserData = async () => {
      const token = getToken();

      for (const user of users) {
        const userUuid = user.uuid || user.authServiceUuid;

        if (
          userUuid &&
          !user.firstName &&
          !user.emailAddress &&
          !user.email &&
          !fetchedUsersRef.current[userUuid] &&
          !loadingUsersRef.current[userUuid]
        ) {
          setLoadingUsers((prev) => ({ ...prev, [userUuid]: true }));

          try {
            if (!token) return;
            const userData = await fetchUserByUuid(userUuid, token, abortController.signal);
            if (abortController.signal.aborted) {
              return;
            }

            setFetchedUsers((prev) => ({ ...prev, [userUuid]: userData }));
          } catch (err) {
            // Background fetch failed - user data will remain incomplete
          } finally {
            setLoadingUsers((prev) => ({ ...prev, [userUuid]: false }));
          }
        }
      }
    };

    void fetchMissingUserData();

    return () => {
      abortController.abort();
    };
  }, [users]);

  const handleImpersonateUser = async (userUuid: string) => {
    if (!userUuid) return;

    setImpersonating(userUuid);
    setImpersonationError(null);
    try {
      const token = getToken();
      const response = await fetch(`${config.GATEWAY_URL}/api/users/user-source/subject-token-login-url`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uuid: userUuid }),
      });

      if (!response.ok) {
        throw new Error(`Failed to get impersonation URL: ${response.statusText}`);
      }

      const data: { message: string } = await response.json();
      window.open(data.message, '_blank');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to impersonate user';
      setImpersonationError(errorMsg);
      setTimeout(() => setImpersonationError(null), 5000);
    } finally {
      setImpersonating(null);
    }
  };

  if (users.length === 0) {
    return (
      <div className="user-info-container">
        <div className="user-info-header">
          <h3>👤 User Information</h3>
          <span className="user-count">No users found</span>
        </div>
      </div>
    );
  }

  return (
    <div className="user-info-container">
      <div className="user-info-header">
        <h3>👤 User Information</h3>
        <span className="user-count">
          {count} {count === 1 ? 'user' : 'users'}
        </span>
      </div>
      {context && <p className="user-info-context">{context}</p>}
      {impersonationError && (
        <div className="mb-4">
          <Alert variant="filled" type="error" full description={impersonationError} />
        </div>
      )}

      <div className="users-grid">
        {users.map((user, index) => {
          const userUuid = user.uuid || user.authServiceUuid;
          const fetchedUser = userUuid ? fetchedUsers[userUuid] : null;
          const isLoading = userUuid ? loadingUsers[userUuid] : false;

          // Merge fetched data with provided data
          const displayUser = fetchedUser
            ? {
                ...user,
                firstName: fetchedUser.firstName,
                lastName: fetchedUser.lastName,
                emailAddress: fetchedUser.emailAddress,
                primaryPhoneNumber: fetchedUser.primaryPhoneNumber,
                secondaryPhoneNumber: fetchedUser.secondaryPhoneNumber,
                source: fetchedUser.source,
                datacenters: fetchedUser.datacenters,
                lastLogin: fetchedUser.lastLogin,
                isAccountLocked: fetchedUser.isAccountLocked,
                fullName: `${fetchedUser.firstName} ${fetchedUser.lastName}`,
              }
            : user;

          const userEmail = displayUser.email || displayUser.emailAddress;
          const isImpersonating = impersonating === userUuid;

          return (
            <div key={user.id || userUuid || index} className="user-card">
              <div className="user-card-header">
                <div className="user-avatar">{displayUser.fullName.charAt(0).toUpperCase()}</div>
                <div className="user-name-section">
                  <div className="flex items-center gap-2">
                    <h4 className="user-name m-0">{isLoading ? 'Loading...' : displayUser.fullName}</h4>
                    {userUuid && (
                      <a
                        href={`/users/${userUuid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 no-underline text-xs px-2 py-0.5 border border-blue-600 rounded bg-blue-50"
                        title="View full user details"
                      >
                        View Details →
                      </a>
                    )}
                  </div>
                  {userEmail && (
                    <div className="user-email">
                      <span className="email-icon">✉️</span>
                      <a href={`mailto:${userEmail}`}>{userEmail}</a>
                      {displayUser.emailVerified && (
                        <span className="verified-badge" title="Email verified">
                          ✓
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="user-details">
                {userUuid && (
                  <div className="user-detail-item">
                    <span className="detail-label">UUID:</span>
                    <span className="detail-value font-mono text-xs">{userUuid}</span>
                  </div>
                )}

                {displayUser.firstName && displayUser.lastName && (
                  <div className="user-detail-item">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">
                      {displayUser.firstName} {displayUser.lastName}
                    </span>
                  </div>
                )}

                {displayUser.primaryPhoneNumber && (
                  <div className="user-detail-item">
                    <span className="detail-label">Primary Phone:</span>
                    <span className="detail-value">{displayUser.primaryPhoneNumber}</span>
                  </div>
                )}

                {displayUser.secondaryPhoneNumber && (
                  <div className="user-detail-item">
                    <span className="detail-label">Secondary Phone:</span>
                    <span className="detail-value">{displayUser.secondaryPhoneNumber}</span>
                  </div>
                )}

                {displayUser.source && (
                  <div className="user-detail-item">
                    <span className="detail-label">Source:</span>
                    <span className="detail-value">{displayUser.source}</span>
                  </div>
                )}

                {displayUser.datacenters && displayUser.datacenters.length > 0 && (
                  <div className="user-detail-item">
                    <span className="detail-label">Datacenters:</span>
                    <span className="detail-value">{displayUser.datacenters.join(', ')}</span>
                  </div>
                )}

                {displayUser.isAccountLocked !== undefined && (
                  <div className="user-detail-item">
                    <span className="detail-label">Account Locked:</span>
                    <span className={`detail-value ${displayUser.isAccountLocked ? 'no' : 'yes'}`}>
                      {displayUser.isAccountLocked ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}

                {displayUser.status !== undefined && (
                  <div className="user-detail-item">
                    <span className="detail-label">Status:</span>
                    <span className="detail-value">{displayUser.status}</span>
                  </div>
                )}

                {displayUser.role && (
                  <div className="user-detail-item">
                    <span className="detail-label">Role:</span>
                    <span className="detail-value">{displayUser.role}</span>
                  </div>
                )}

                {displayUser.industry && (
                  <div className="user-detail-item">
                    <span className="detail-label">Industry:</span>
                    <span className="detail-value">{displayUser.industry}</span>
                  </div>
                )}

                {displayUser.country && (
                  <div className="user-detail-item">
                    <span className="detail-label">Country:</span>
                    <span className="detail-value">{displayUser.country}</span>
                  </div>
                )}
              </div>

              {(displayUser.created || displayUser.updated || displayUser.lastLogin) && (
                <div className="user-timestamps">
                  {displayUser.created && (
                    <div className="timestamp">
                      <span className="timestamp-label">Created:</span>
                      <span className="timestamp-value">{new Date(displayUser.created).toLocaleDateString()}</span>
                    </div>
                  )}
                  {displayUser.updated && (
                    <div className="timestamp">
                      <span className="timestamp-label">Updated:</span>
                      <span className="timestamp-value">{new Date(displayUser.updated).toLocaleDateString()}</span>
                    </div>
                  )}
                  {displayUser.lastLogin && (
                    <div className="timestamp">
                      <span className="timestamp-label">Last Login:</span>
                      <span className="timestamp-value">{new Date(displayUser.lastLogin).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              )}

              {canImpersonate && userUuid && (
                <div className="user-actions mt-3 pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => handleImpersonateUser(userUuid)}
                    disabled={isImpersonating}
                    className={`w-full py-2 px-4 rounded text-sm font-medium border-none ${
                      isImpersonating
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-yellow-500 text-white cursor-pointer hover:bg-yellow-600'
                    }`}
                  >
                    {isImpersonating ? 'Impersonating...' : 'Impersonate User'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
